import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  SafeAreaView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation, useRoute } from "@react-navigation/native";
import type { RouteProp } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type RootStackParamList from "../navigation/RootStackParamList";
import { useAppSelector } from "../redux/store";
import { getConversationAttendance } from "../services/adminConversationsService";
import type { AttendanceUser, RoomAttendance } from "../utils/roomAttendance";

type Filter = "all" | "noShows" | "empty";

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "noShows", label: "Has no-shows" },
  { key: "empty", label: "No sign-ups" },
];

const formatDate = (date: Date | null): string =>
  date
    ? date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "No date";

function PersonRow({
  person,
  walkIn,
  upcoming,
}: {
  person: AttendanceUser;
  walkIn?: boolean;
  upcoming?: boolean;
}) {
  // An upcoming conversation has no no-shows — those people simply have not
  // had the chance to attend yet.
  const tone = person.joined
    ? styles.badgeJoined
    : upcoming
      ? styles.badgePending
      : styles.badgeNoShow;
  const textTone = person.joined
    ? styles.badgeTextJoined
    : upcoming
      ? styles.badgeTextPending
      : styles.badgeTextNoShow;

  const label = walkIn
    ? "Joined, no sign-up"
    : person.joined
      ? "Joined"
      : upcoming
        ? "Signed up"
        : "Did not join";

  return (
    <View style={styles.personRow}>
      <View style={styles.personDetails}>
        <Text style={styles.personName}>{person.name}</Text>
        {person.email ? (
          <Text style={styles.personEmail}>{person.email}</Text>
        ) : null}
      </View>
      <View style={[styles.badge, tone]}>
        <Text style={[styles.badgeText, textTone]}>{label}</Text>
      </View>
    </View>
  );
}

export default function AdminConversationsScreen() {
  const isAdmin = useAppSelector((state) => state.user.admin);
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "AdminConversations">>();

  const [rooms, setRooms] = useState<RoomAttendance[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(
    route.params?.initialSearch ?? "",
  );
  const [activeFilter, setActiveFilter] = useState<Filter>("all");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setErrorText(null);
    try {
      setRooms(await getConversationAttendance());
    } catch (error) {
      console.error("Failed to load conversation attendance:", error);
      setErrorText("Failed to load conversations.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let result = rooms;

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (room) =>
          room.name.toLowerCase().includes(q) ||
          room.ownerName.toLowerCase().includes(q) ||
          room.signedUp.some(
            (person) =>
              person.name.toLowerCase().includes(q) ||
              person.email.toLowerCase().includes(q),
          ),
      );
    }

    if (activeFilter === "noShows") {
      result = result.filter(
        (room) => !room.isUpcoming && room.noShowCount > 0,
      );
    } else if (activeFilter === "empty") {
      result = result.filter((room) => room.signedUp.length === 0);
    }

    return result;
  }, [rooms, searchQuery, activeFilter]);

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.screen}>
        <View style={styles.accessState}>
          <Ionicons name="lock-closed-outline" size={28} color="#3a6fb1" />
          <Text style={styles.title}>Access restricted</Text>
          <Text style={styles.subtitle}>
            This page is only available to administrators.
          </Text>
          <Pressable
            style={styles.primaryButton}
            onPress={() => navigation.navigate("MeetingsTabs")}
          >
            <Text style={styles.primaryButtonText}>Back to meetings</Text>
          </Pressable>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.screen}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Pressable
            style={styles.backButton}
            onPress={() =>
              navigation.canGoBack()
                ? navigation.goBack()
                : navigation.navigate("MeetingsTabs")
            }
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <Ionicons name="chevron-back" size={24} color="#3a6fb1" />
          </Pressable>
          <View>
            <Text style={styles.title}>Conversation attendance</Text>
            <Text style={styles.subtitle}>
              {filtered.length} of {rooms.length} conversations
            </Text>
          </View>
        </View>
        <Pressable
          style={styles.refreshButton}
          onPress={load}
          accessibilityRole="button"
          accessibilityLabel="Refresh conversation attendance"
        >
          <Ionicons name="refresh" size={18} color="#3a6fb1" />
        </Pressable>
      </View>

      <View style={styles.searchContainer}>
        <Ionicons
          name="search"
          size={18}
          color="rgba(43,43,47,0.4)"
          style={styles.searchIcon}
        />
        <TextInput
          style={styles.searchInput}
          placeholder="Search by conversation, host or person"
          placeholderTextColor="rgba(43,43,47,0.4)"
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {searchQuery ? (
          <Pressable
            style={styles.clearButton}
            onPress={() => setSearchQuery("")}
          >
            <Ionicons name="close" size={16} color="rgba(43,43,47,0.5)" />
          </Pressable>
        ) : null}
      </View>

      <View style={styles.filterRow}>
        {FILTERS.map((filter) => (
          <Pressable
            key={filter.key}
            style={[
              styles.filterChip,
              activeFilter === filter.key && styles.filterChipActive,
            ]}
            onPress={() => setActiveFilter(filter.key)}
          >
            <Text
              style={[
                styles.filterChipText,
                activeFilter === filter.key && styles.filterChipTextActive,
              ]}
            >
              {filter.label}
            </Text>
          </Pressable>
        ))}
      </View>

      {loading ? (
        <ActivityIndicator style={styles.loader} color="#3a6fb1" />
      ) : null}
      {errorText ? <Text style={styles.errorText}>{errorText}</Text> : null}

      <FlatList
        data={filtered}
        keyExtractor={(room) => room.roomId}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          loading ? null : (
            <Text style={styles.emptyText}>No conversations to show.</Text>
          )
        }
        renderItem={({ item }) => {
          const isOpen = expanded[item.roomId];
          const hasDetail = item.signedUp.length > 0 || item.walkIns.length > 0;

          return (
            <View style={styles.roomCard}>
              <Pressable
                style={styles.roomHeader}
                onPress={() =>
                  setExpanded((prev) => ({
                    ...prev,
                    [item.roomId]: !prev[item.roomId],
                  }))
                }
              >
                <View style={styles.roomDetails}>
                  <Text style={styles.roomName}>{item.name}</Text>
                  <Text style={styles.roomMeta}>
                    {formatDate(item.startAt)} · hosted by {item.ownerName}
                  </Text>
                  <Text style={styles.roomStats}>
                    {item.signedUp.length} signed up
                    {item.isUpcoming ? (
                      <Text style={styles.upcomingStat}> · upcoming</Text>
                    ) : (
                      <>
                        {` · ${item.joinedCount} joined · `}
                        <Text
                          style={
                            item.noShowCount > 0 ? styles.noShowStat : undefined
                          }
                        >
                          {item.noShowCount} no-show
                        </Text>
                      </>
                    )}
                    {item.walkIns.length > 0
                      ? ` · +${item.walkIns.length} without signing up`
                      : ""}
                  </Text>
                </View>
                {hasDetail ? (
                  <Ionicons
                    name={isOpen ? "chevron-up" : "chevron-down"}
                    size={18}
                    color="rgba(43,43,47,0.5)"
                  />
                ) : null}
              </Pressable>

              {isOpen && hasDetail ? (
                <View style={styles.peopleSection}>
                  {item.signedUp.map((person) => (
                    <PersonRow
                      key={person.uid}
                      person={person}
                      upcoming={item.isUpcoming}
                    />
                  ))}
                  {item.walkIns.map((person) => (
                    <PersonRow key={person.uid} person={person} walkIn />
                  ))}
                </View>
              ) : null}
            </View>
          );
        }}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: "#F6F7F9",
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  header: {
    width: "100%",
    maxWidth: 900,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: 24,
    paddingBottom: 16,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexShrink: 1,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "rgba(58, 111, 177, 0.18)",
  },
  title: {
    fontFamily: "Lexend",
    fontSize: 30,
    fontWeight: "700",
    color: "#2B2B2F",
  },
  subtitle: {
    marginTop: 6,
    fontFamily: "Lexend",
    fontSize: 14,
    color: "rgba(43, 43, 47, 0.62)",
  },
  refreshButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "rgba(58, 111, 177, 0.18)",
  },
  searchContainer: {
    width: "100%",
    maxWidth: 900,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(58, 111, 177, 0.18)",
    paddingHorizontal: 12,
    height: 44,
  },
  searchIcon: { marginRight: 8 },
  searchInput: {
    flex: 1,
    fontFamily: "Lexend",
    fontSize: 14,
    color: "#2B2B2F",
    outlineStyle: "none",
  } as object,
  clearButton: { padding: 4 },
  filterRow: {
    width: "100%",
    maxWidth: 900,
    alignSelf: "center",
    flexDirection: "row",
    gap: 8,
    marginTop: 12,
    marginBottom: 4,
  },
  filterChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "rgba(58, 111, 177, 0.18)",
  },
  filterChipActive: { backgroundColor: "#3a6fb1", borderColor: "#3a6fb1" },
  filterChipText: {
    fontFamily: "Lexend",
    fontSize: 13,
    color: "rgba(43, 43, 47, 0.72)",
  },
  filterChipTextActive: { color: "#fff" },
  loader: { marginTop: 16 },
  listContent: {
    width: "100%",
    maxWidth: 900,
    alignSelf: "center",
    paddingTop: 12,
    gap: 10,
  },
  roomCard: {
    backgroundColor: "#fff",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "rgba(58, 111, 177, 0.14)",
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  roomHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  roomDetails: { flex: 1 },
  roomName: {
    fontFamily: "LexendSemiBold",
    fontSize: 16,
    color: "#2B2B2F",
  },
  roomMeta: {
    marginTop: 3,
    fontFamily: "Lexend",
    fontSize: 12,
    color: "rgba(43, 43, 47, 0.55)",
  },
  roomStats: {
    marginTop: 6,
    fontFamily: "Lexend",
    fontSize: 13,
    color: "rgba(43, 43, 47, 0.72)",
  },
  noShowStat: { color: "#C0392B", fontFamily: "LexendSemiBold" },
  upcomingStat: { color: "#3a6fb1", fontFamily: "LexendSemiBold" },
  peopleSection: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "rgba(58, 111, 177, 0.12)",
    gap: 8,
  },
  personRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  personDetails: { flex: 1 },
  personName: {
    fontFamily: "Lexend",
    fontSize: 14,
    color: "#2B2B2F",
  },
  personEmail: {
    fontFamily: "Lexend",
    fontSize: 12,
    color: "rgba(43, 43, 47, 0.5)",
  },
  badge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
  },
  badgeJoined: { backgroundColor: "rgba(39, 174, 96, 0.12)" },
  badgeNoShow: { backgroundColor: "rgba(192, 57, 43, 0.10)" },
  badgePending: { backgroundColor: "rgba(58, 111, 177, 0.10)" },
  badgeText: { fontFamily: "Lexend", fontSize: 11 },
  badgeTextJoined: { color: "#1E8449" },
  badgeTextNoShow: { color: "#C0392B" },
  badgeTextPending: { color: "#3a6fb1" },
  emptyText: {
    marginTop: 24,
    textAlign: "center",
    fontFamily: "Lexend",
    fontSize: 14,
    color: "rgba(43, 43, 47, 0.55)",
  },
  errorText: {
    marginTop: 12,
    fontFamily: "Lexend",
    fontSize: 13,
    color: "#C0392B",
    textAlign: "center",
  },
  accessState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  primaryButton: {
    marginTop: 8,
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: "#3a6fb1",
  },
  primaryButtonText: {
    fontFamily: "Lexend",
    fontSize: 14,
    color: "#fff",
  },
});
